import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { motion, useReducedMotion } from "framer-motion";
import { documentRequest } from "../../../settings/documentSettingsApi";
import {
  DocumentDialog,
  DOC_TYPES,
  Choice,
  CustomerEditor,
  Field,
  SearchPicker,
  Section,
  money,
  nameOf,
} from "./DocumentUI";

export function GenerateDocument({ token, onClose, onDone, repair }) {
  const [step, setStep] = useState(0),
    [kind, setKind] = useState(repair?.doc_type || "invoice"),
    [payment, setPayment] = useState(repair?.payment_type || "prepay");
  const [event, setEvent] = useState(null),
    [context, setContext] = useState(null),
    [client, setClient] = useState(null),
    [saved, setSaved] = useState(false),
    [clientMode, setClientMode] = useState("search");
  const [preview, setPreview] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [forceNew, setForceNew] = useState(false);
  const reduce = useReducedMotion(),
    eventSeq = useRef(0);
  const pickEvent = async (e) => {
    const id = ++eventSeq.current;
    setEvent(e);
    setContext(null);
    setClient(null);
    setSaved(false);
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      const c = await documentRequest(`events/${e.id}/context/`, { token });
      if (id !== eventSeq.current) return;
      setContext(c);
      setEvent(c);
      setClient(
        c.client || (c.candidates?.length === 1 ? c.candidates[0] : null),
      );
      setSaved(!!c.client);
      setClientMode(c.client || c.candidates?.length === 1 ? "edit" : "search");
    } catch (err) {
      if (id === eventSeq.current) setError(err.message);
    } finally {
      if (id === eventSeq.current) setBusy(false);
    }
  };
  useEffect(() => {
    if (repair?.event) pickEvent({ id: repair.event });
    else if (repair?.client)
      documentRequest(`customers/${repair.client}/`, { token })
        .then((c) => {
          setClient(c);
          setSaved(true);
          setClientMode("edit");
        })
        .catch((e) => setError(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const payload = () => ({
    doc_type: kind,
    event_id: event?.id,
    client_id: client?.id,
    payment_type: ["invoice", "invoice_contract"].includes(kind)
      ? payment
      : undefined,
    force_new: forceNew,
  });
  const next = async () => {
    setError("");
    if (step === 0) {
      if (!event && !["contract", "edo_agreement"].includes(kind)) {
        setError("Выберите объект из результатов поиска");
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!client?.id || !saved) {
        setError("Сохраните реквизиты заказчика и связь с объектом");
        return;
      }
      setBusy(true);
      try {
        const p = await documentRequest("readiness/", {
          token,
          method: "POST",
          data: payload(),
        });
        setPreview(p);
        setStep(2);
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      await documentRequest(repair ? `${repair.id}/regenerate/` : "generate/", {
        token,
        method: "POST",
        data: repair ? {} : payload(),
      });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const pickClient = (c) => {
    setClient(c);
    setSaved(false);
    setClientMode("edit");
    setError("");
  };
  return (
    <DocumentDialog
      wide
      title={repair ? "Исправить и сформировать документ" : "Новый документ"}
      subtitle="Выберите объект, проверьте заказчика и сформируйте файл на сайте."
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button
            variant="plain"
            color="neutral"
            disabled={busy}
            onClick={() => (step ? setStep((s) => s - 1) : onClose())}
          >
            {step ? "Назад" : "Отмена"}
          </Button>
          <Button
            loading={busy}
            disabled={step === 2 && !preview?.ready}
            onClick={next}
          >
            {step === 2
              ? repair
                ? "Повторить формирование"
                : "Сформировать документ"
              : "Продолжить"}
          </Button>
        </>
      }
    >
      <div className="doc-stepper">
        {["Объект и документ", "Заказчик", "Проверка"].map((x, i) => (
          <div key={x} className={`doc-step ${step === i ? "active" : ""}`}>
            <b>{i + 1}</b>
            {x}
          </div>
        ))}
      </div>
      <motion.div
        key={step}
        initial={{ opacity: 0, x: reduce ? 0 : 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: reduce ? 0 : 0.18 }}
      >
        <Stack gap={2.5}>
          {step === 0 && (
            <>
              <div className="doc-grid">
                <Choice
                  label="Тип документа"
                  value={kind}
                  onChange={setKind}
                  options={DOC_TYPES}
                  disabled={!!repair}
                />
                {["invoice", "invoice_contract"].includes(kind) && (
                  <Choice
                    label="Оплата"
                    value={payment}
                    onChange={setPayment}
                    options={[
                      ["prepay", "Предоплата"],
                      ["postpay", "Постоплата"],
                    ]}
                    disabled={!!repair}
                  />
                )}
              </div>
              {repair ? (
                <Card variant="soft">
                  <Typography level="title-sm">
                    {event?.address ||
                      (repair.event
                        ? `Событие №${repair.event}`
                        : "Документ без объекта")}
                  </Typography>
                  <Typography level="body-sm">
                    Повторное формирование сохраняет исходный объект.
                  </Typography>
                </Card>
              ) : (
                <SearchPicker
                  token={token}
                  value={event}
                  onSelect={pickEvent}
                  onClear={() => {
                    ++eventSeq.current;
                    setEvent(null);
                    setContext(null);
                    setClient(null);
                    setSaved(false);
                    setBusy(false);
                  }}
                />
              )}{" "}
              {busy && <CircularProgress size="sm" />}
              {["contract", "edo_agreement"].includes(kind) && (
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  Договор и соглашение можно сформировать на клиента без выбора
                  объекта.
                </Typography>
              )}
              {event && (
                <Alert color={event.client ? "success" : "warning"}>
                  {event.client
                    ? `Заказчик: ${nameOf(event.client)}`
                    : "У объекта пока нет заказчика. На следующем шаге найдите аккаунт или заполните реквизиты."}
                </Alert>
              )}
              {repair && (
                <Typography level="body-xs">
                  Повтор использует номер и дату выбранного документа.
                </Typography>
              )}
            </>
          )}
          {step === 1 && (
            <>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant={clientMode === "search" ? "solid" : "soft"}
                  onClick={() => setClientMode("search")}
                >
                  Найти в базе
                </Button>
                <Button
                  variant={clientMode === "new" ? "solid" : "soft"}
                  onClick={() => {
                    setClient(null);
                    setSaved(false);
                    setClientMode("new");
                  }}
                >
                  Заполнить нового
                </Button>
                {client && (
                  <Button
                    variant={clientMode === "edit" ? "solid" : "soft"}
                    onClick={() => setClientMode("edit")}
                  >
                    Реквизиты выбранного
                  </Button>
                )}
              </Stack>
              {context?.client_conflict && (
                <Alert color="danger">{context.client_conflict}</Alert>
              )}
              {clientMode === "search" && (
                <>
                  <SearchPicker
                    kind="clients"
                    token={token}
                    value={client}
                    onSelect={pickClient}
                  />
                  {!!context?.candidates?.length && (
                    <Section
                      title="Совпадения по телефону объекта"
                      description="Выберите аккаунт и проверьте, кто является заказчиком."
                    >
                      {context.candidates.map((c) => (
                        <Button
                          key={c.id}
                          variant="outlined"
                          onClick={() => pickClient(c)}
                        >
                          {nameOf(c)} · {c.phone}
                        </Button>
                      ))}
                    </Section>
                  )}
                </>
              )}
              {clientMode !== "search" && (
                <CustomerEditor
                  client={clientMode === "new" ? null : client}
                  event={event}
                  contact={context?.contact}
                  token={token}
                  onDirty={() => setSaved(false)}
                  onSaved={(c) => {
                    setClient(c);
                    setSaved(true);
                    setClientMode("edit");
                  }}
                />
              )}
              {saved && (
                <Alert color="success">
                  Заказчик сохранён. Можно перейти к проверке документа.
                </Alert>
              )}
            </>
          )}
          {step === 2 && preview && (
            <>
              <Card variant="soft" sx={{ borderRadius: "16px" }}>
                <Typography level="title-lg">Перед формированием</Typography>
                {[
                  ["Документ", DOC_TYPES.find(([v]) => v === kind)?.[1]],
                  ["Заказчик", nameOf(preview.client)],
                  ["Объект", preview.address || "Без привязки к объекту"],
                  ["Исполнитель", preview.seller || "Не указан"],
                  ["Сумма", money(preview.amount)],
                  ["Шаблон", preview.template],
                ].map(([k, v]) => (
                  <div className="doc-summary-line" key={k}>
                    <span>{k}</span>
                    <strong>{v}</strong>
                  </div>
                ))}
              </Card>
              {preview.errors.map((e) => (
                <Alert key={e} color="danger">
                  {e}
                </Alert>
              ))}
              {preview.warnings.map((e) => (
                <Alert key={e} color="warning">
                  {e}
                </Alert>
              ))}
              {!repair && (
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Switch
                    checked={forceNew}
                    onChange={(e) => setForceNew(e.target.checked)}
                  />
                  <Typography level="body-sm">
                    Создать новую версию, даже если документ уже есть
                  </Typography>
                </Stack>
              )}
              <Alert color="neutral">
                Формирование создаёт файл только на сайте. Выдача клиенту и
                передача в Диадок выполняются отдельно.
              </Alert>
            </>
          )}
          {error && <Alert color="danger">{error}</Alert>}
        </Stack>
      </motion.div>
    </DocumentDialog>
  );
}

export function BundleDocument({ token, onClose, onDone }) {
  const [client, setClient] = useState(null),
    [events, setEvents] = useState([]),
    [payment, setPayment] = useState("postpay"),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await documentRequest("bundle/", {
        token,
        method: "POST",
        data: {
          client_id: client.id,
          event_ids: events.map((e) => e.id),
          payment_type: payment,
        },
      });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <DocumentDialog
      wide
      title="Один счёт на несколько объектов"
      subtitle="Выберите заказчика и добавьте его объекты в общий счёт."
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Отмена
          </Button>
          <Button
            disabled={!client || !events.length}
            loading={busy}
            onClick={submit}
          >
            Сформировать ·{" "}
            {money(events.reduce((s, e) => s + Number(e.amount || 0), 0))}
          </Button>
        </>
      }
    >
      <Stack gap={2.5}>
        <SearchPicker
          kind="clients"
          token={token}
          value={client}
          onSelect={(c) => {
            setClient(c);
            setEvents([]);
          }}
        />
        {client && (
          <SearchPicker
            token={token}
            clientId={client.id}
            exclude={events.map((e) => e.id)}
            label="Добавить объект заказчика"
            onSelect={(e) => setEvents((v) => [...v, e])}
          />
        )}
        {events.map((e) => (
          <Card key={e.id}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Box>
                <Typography level="title-sm">{e.address}</Typography>
                <Typography level="body-xs">
                  Сделка №{e.deal_id} · {money(e.amount)}
                </Typography>
              </Box>
              <Button
                variant="plain"
                color="danger"
                size="sm"
                onClick={() => setEvents((v) => v.filter((x) => x.id !== e.id))}
              >
                Убрать
              </Button>
            </Stack>
          </Card>
        ))}
        <Choice
          label="Тип оплаты"
          value={payment}
          onChange={setPayment}
          options={[
            ["prepay", "Предоплата"],
            ["postpay", "Постоплата"],
          ]}
        />
        {error && <Alert color="danger">{error}</Alert>}
      </Stack>
    </DocumentDialog>
  );
}
export function DeliverDocument({ token, doc, onClose, onDone }) {
  const [method, setMethod] = useState("lk"),
    [email, setEmail] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await documentRequest(`${doc.id}/deliver/`, {
        token,
        method: "POST",
        data: { method, email },
      });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <DocumentDialog
      title="Выдать документ"
      subtitle={`${doc.doc_type_display} № ${doc.number}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Отмена
          </Button>
          <Button loading={busy} onClick={submit}>
            {method === "edo"
              ? "Выдать и поставить в очередь ЭДО"
              : "Выдать документ"}
          </Button>
        </>
      }
    >
      <Stack gap={2}>
        <Choice
          label="Способ выдачи"
          value={method}
          onChange={setMethod}
          options={[
            ["lk", "Личный кабинет клиента"],
            ["email", "Личный кабинет и письмо с PDF"],
            ["manager", "Передать менеджеру"],
            ["edo", "Кабинет клиента и черновик Диадока"],
          ]}
        />
        {method === "email" && (
          <Field
            label="Email получателя"
            type="email"
            value={email}
            onChange={setEmail}
          />
        )}
        {method === "edo" && (
          <Alert color="warning">
            Будет опубликован документ в кабинете клиента и создано задание
            передачи в Диадок. Руководитель отдельно подписывает и отправляет
            черновик.
          </Alert>
        )}
        {error && <Alert color="danger">{error}</Alert>}
      </Stack>
    </DocumentDialog>
  );
}
